import classNames from 'classnames';

import type {FileInfo} from '../../../utils/attachments/types';
import {EmbeddedImage} from '../../embedded-image';
import {MenuDownIcon} from '../../../icons/menu-down';
import {MenuRightIcon} from '../../../icons/menu-right';
import {TypographySize, useTypography} from '../../../hooks/typography';

import styles from './styles.module.css';

type Props = {
    fileInfo: FileInfo;
    interactive?: boolean;
    isInPermalink?: boolean;
    compactDisplay?: boolean;
    isEmbedVisible?: boolean;
    enablePublicLink?: boolean;
    canDownloadFile?: boolean;
    className?: string;
    getFileUrl: (id: string) => string;
    getFilePreviewUrl: (id: string) => string;
    getFilePublicLink?: (fileId: string) => Promise<string>;
    toggleEmbedVisibility?: () => void;
    handleImageClick?: (fileInfo: FileInfo) => void;
    onCopyToClipboard?: (src: string) => void;
    onDownload?: () => void;
};

export const SingleImageView = (props: Props) => {
    const {
        fileInfo,
        compactDisplay,
        isInPermalink,
        getFileUrl,
        getFilePreviewUrl,
        isEmbedVisible,
        toggleEmbedVisibility,
        interactive,
        handleImageClick,
        enablePublicLink,
        canDownloadFile,
        getFilePublicLink,
        onCopyToClipboard,
        className,
        onDownload,
    } = props;
    const bodyCls = useTypography({
        size: TypographySize.BodyS,
        noDefaultColor: true,
    });

    const onImageClick = () => {
        if (!interactive) {
            return;
        }

        handleImageClick?.(fileInfo);
    };
    const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        onImageClick();
    };
    const handleToggleEmbedVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        toggleEmbedVisibility?.();
    };

    return (
        <div className={classNames(className, styles.singleImageView, {[styles.permalink]: isInPermalink})}>
            <div
                className={classNames(styles.header, {
                    [styles.expanded]: isEmbedVisible,
                })}
            >
                <button
                    type='button'
                    key='toggle'
                    className={styles.toggleButton}
                    data-expanded={isEmbedVisible}
                    aria-label='Toggle Embed Visibility'
                    onClick={handleToggleEmbedVisibility}
                >
                    {isEmbedVisible && <MenuDownIcon size={{width: 20, height: 22}} />}
                    {!isEmbedVisible && <MenuRightIcon size={{width: 18, height: 18}} />}
                </button>
                {!isEmbedVisible && (
                    <div className={classNames(styles.imageName, bodyCls)} onClick={handleHeaderClick}>
                        {fileInfo.name}
                    </div>
                )}
            </div>
            {isEmbedVisible && (
                <div
                    className={classNames(styles.embeddedContainer, {
                        [styles.permalinkImage]: isInPermalink,
                        [styles.compact]: compactDisplay,
                    })}
                >
                    <EmbeddedImage
                        fileInfo={fileInfo}
                        getFileUrl={getFileUrl}
                        getFilePreviewUrl={getFilePreviewUrl}
                        onClick={onImageClick}
                        getFilePublicUrl={getFilePublicLink}
                        enableDownload={canDownloadFile}
                        enableCopy={enablePublicLink}
                        onCopy={onCopyToClipboard}
                        onDownload={onDownload}
                    />
                </div>
            )}
        </div>
    );
};
